// Persistent account state: public profile metadata as plain records, the
// identity seed and refresh token as wrapped secrets (docs/03 §7 — "tokens in
// memory + refresh token in platform-secure storage; wipe on logout").

import { keysFromSeed, type IdentityKeys } from '../identity/keys.js';
import type { KvStore } from './kv.js';
import { REFRESH_SECRET_KEY as REFRESH_SECRET } from './kvKeys.js';
import { SecretStore, type SecretCipher } from './secretStore.js';

const ACCOUNT_KEY = 'account';
const SEED_SECRET = 'identity-seed';
// The password-derived authKey (2FA proof, docs/03 §3a), kept next to the
// refresh token in secure storage so silent re-login can attach it without
// re-prompting. Never the password itself — only the HKDF authKey branch.
const AUTHKEY_SECRET = 'password-authkey';

export interface StoredAccount {
  /** Assigned by `POST /register`; absent while registration is pending. */
  userId?: string;
  displayName: string;
  publicKeyB64: string;
  /** True when this identity was restored from a recovery phrase. */
  restored: boolean;
  createdAt: string;
  /**
   * The chosen liquid-mercury avatar (candidate index 0..8), or absent when the
   * initials look is in use. Stored locally so the picked avatar renders on a
   * cold, offline boot without waiting on `GET /me` (prime directive).
   */
  avatarSeed?: number;
}

export class AccountStore {
  private constructor(
    private readonly kv: KvStore,
    private readonly secrets: SecretStore,
  ) {}

  /** `cipher` selects the platform secret wrapping (Keystore on Android). */
  static async open(kv: KvStore, cipher?: SecretCipher): Promise<AccountStore> {
    return new AccountStore(kv, await SecretStore.open(kv, cipher));
  }

  loadAccount(): Promise<StoredAccount | undefined> {
    return this.kv.get<StoredAccount>(ACCOUNT_KEY);
  }

  async saveAccount(account: StoredAccount): Promise<void> {
    await this.kv.put(ACCOUNT_KEY, account);
  }

  /**
   * Persist the chosen avatar seed onto the stored account, returning the
   * updated record (or undefined when no account exists yet). A no-op when the
   * seed is unchanged, so a `GET /me` hydration that agrees with local state
   * never churns the account snapshot. `null` clears the choice.
   */
  async setAvatarSeed(seed: number | null): Promise<StoredAccount | undefined> {
    const account = await this.loadAccount();
    if (!account) return undefined;
    const normalized = seed ?? undefined;
    if (account.avatarSeed === normalized) return account;
    const updated: StoredAccount = { ...account };
    if (normalized === undefined) delete updated.avatarSeed;
    else updated.avatarSeed = normalized;
    await this.saveAccount(updated);
    return updated;
  }

  async saveIdentity(identity: IdentityKeys): Promise<void> {
    await this.secrets.put(SEED_SECRET, identity.seed);
  }

  async loadIdentity(): Promise<IdentityKeys | undefined> {
    const seed = await this.secrets.get(SEED_SECRET);
    return seed ? keysFromSeed(seed) : undefined;
  }

  async saveRefreshToken(token: string): Promise<void> {
    await this.secrets.put(REFRESH_SECRET, new TextEncoder().encode(token));
  }

  async loadRefreshToken(): Promise<string | undefined> {
    const bytes = await this.secrets.get(REFRESH_SECRET);
    return bytes ? new TextDecoder().decode(bytes) : undefined;
  }

  async deleteRefreshToken(): Promise<void> {
    await this.secrets.delete(REFRESH_SECRET);
  }

  /** Persist the password-derived authKey (2FA proof) in secure storage. */
  async saveAuthKey(authKey: Uint8Array): Promise<void> {
    await this.secrets.put(AUTHKEY_SECRET, authKey);
  }

  async loadAuthKey(): Promise<Uint8Array | undefined> {
    return this.secrets.get(AUTHKEY_SECRET);
  }

  async deleteAuthKey(): Promise<void> {
    await this.secrets.delete(AUTHKEY_SECRET);
  }

  /** Full local wipe (logout / account deletion). */
  async wipe(): Promise<void> {
    await this.kv.clear();
  }
}
